import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useDebounce } from "use-debounce";
import styles from "./SearchBar.module.scss";
import useSearch from "hooks/useSearch";
import Chevron from "assets/Chevron";
import useCount from "hooks/useCount";
import MenuIcon from "assets/MenuIcon";
import { db, IUserSite } from "database";
import { getEnvs } from "../../env";

type SearchBarProps = {
	selectedFilter: string;
	status?: boolean;
	openTab: () => void;
	refreshSearch: boolean;
	refreshData: () => void;
	createNewSite: () => void;
	close: () => void;
	filterChange: (status: string) => void;
	openSideMenu: () => void;
	onSiteSelected: (siteId: IUserSite) => void;
	changeMapLocation: (latitude: string, longitude: string) => void;
};

const SearchBar: React.FC<SearchBarProps> = ({
	status,
	close,
	refreshData,
	refreshSearch,
	selectedFilter,
	createNewSite,
	filterChange,
	openTab,
	openSideMenu,
	onSiteSelected,
	changeMapLocation,
}) => {
	let isActive = null;
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText] = useDebounce(searchText, 500);
	const [SERVER_URL, setSERVER_URL] = useState("");

	useEffect(() => {
		async function setServerUrl() {
			try {
				const envvars: any = await getEnvs();
				setSERVER_URL(envvars.REACT_APP_SERVER_URL_API);
			} catch {
				setSERVER_URL("http://localhost:4000");
			}
		}

		setServerUrl();
	}, []);

	const exportVerifiedData = async () => {
		const verifiedUsers = await db.userSites.where({ status: "verified" });
		const dataToExport = await verifiedUsers.toArray();
		if (dataToExport.length > 0) {
			const sortingOrder = [
				"siteId",
				"userSiteId",
				"route",
				"name",
				"altName",
				"siteType",
				"operationalStatus",
				"latitude",
				"longitude",
				"elr",
				"mileage",
				"postcode",
				"username",
				"comments",
			];
			const headers = [
				"Site Id",
				"Record Id",
				"Route",
				"Site Name",
				"Alternative Site Name",
				"Site Type",
				"Site Operational Status",
				"Latitude",
				"Longitude",
				"Site ELR",
				"Site Mileage",
				"Site Postcode",
				"Username",
				"Comments",
			];

			const cleanInput = (val: any) => {
				if (typeof val === "string") {
					if (val.includes('"')) {
						val = val.replace(/"/g, '""');
					}
					if (
						val.includes(",") ||
						val.includes("\n") ||
						val.includes('"')
					) {
						val = `"${val}"`;
					}
					val = val.replace(/’/g, "'");
				}
				return val;
			}

			let rows = dataToExport.map(function (obj: any) {
				let row: string[] = [];
				sortingOrder.map(function (key) {
					if (key !== "_id" && key !== "photos") {
						row.push(cleanInput(obj[key]));
					}
				});
				return row;
			});

			rows = [headers, ...rows];

			let csvContent =
				"data:text/csv;charset=utf-8," +
				rows.map((e: any) => e.join(",")).join("\n");

			const downloadSiteData = () => {
				if (window.navigator.msSaveBlob) {
					window.navigator.msSaveBlob(
						new Blob([rows.map((e: any) => e.join(",")).join("\n")]),
						`LSB Data Export ${format(new Date(), "dd-MM-yy")}.csv`
					);
				} else {
					var encodedUri = encodeURI(csvContent);
					var link = document.createElement("a");
					link.setAttribute("href", encodedUri);
					link.setAttribute(
						"download",
						`LSB Data Export ${format(new Date(), "dd-MM-yy")}.csv`
					);
					document.body.appendChild(link);
					link.click();
				}
			};

			let response;
			const userSiteIds = dataToExport.map((data) => data.userSiteId);
			try {
				response = await fetch(`${SERVER_URL}/updateSitesToExported`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					credentials: "include",
					body: JSON.stringify({
						userSiteIds,
					}),
				});
				downloadSiteData();
				refreshData();
			} catch (err) {
				console.log(err);
			}
			console.log("no data to export");
		}
	};

	let searchResult = useSearch(debouncedSearchText, selectedFilter);

	const searchChanged = (e: any) => {
		setSearchText(e.target.value);
	};
	const allCount = useCount("all", selectedFilter);
	const awaitingCount = useCount("awaiting verification", selectedFilter);
	const verifiedCount = useCount("verified", selectedFilter);
	const exportedCount = useCount("exported", selectedFilter);

	const filter = [
		{ name: "All", count: allCount, class: "All", status: "all" },
		{
			name: "Awaiting verification",
			count: awaitingCount,
			class: "AwaitingVerification",
			status: "awaiting verification",
		},
		{
			name: "Verified",
			count: verifiedCount,
			class: "Verified",
			status: "verified",
		},
		{
			name: "Exported",
			count: exportedCount,
			class: "Exported",
			status: "exported",
		},
	];

	if (status === true) {
		isActive = styles.SearchBar + " " + styles.activeSearch;
	} else {
		isActive = styles.SearchBar;
	}

	return (
		<div>
			<div className={isActive}>
				<div onClick={() => openSideMenu()}>
					<MenuIcon />
				</div>
				<input
					onClick={() => openTab()}
					type="text"
					value={searchText}
					onChange={(e) => searchChanged(e)}
					placeholder="Search for buildings and locations"
				/>
				{status === true && (
					<button
						className={styles.closeSearch}
						onClick={() => {
							setSearchText("");
							close();
						}}
					/>
				)}
				{status === false && (
					<div className={styles.chevron} onClick={() => openTab()}>
						<Chevron color="grey" />
					</div>
				)}
			</div>
			<div className={styles.ResultsWrapper}>
				<div className={styles.FilterWrapper}>
					<div className={styles.Filters}>
						{filter.map((f, idx) => (
							<div
								className={styles.FilterRow}
								key={idx}
								onClick={() => filterChange(f.status)}
							>
								<div className={`${styles.FilterCount} ${styles[f.class]}`}>
									{f.count}
								</div>
								<p
									className={
										selectedFilter === f.status
											? styles.highlightedFilter
											: styles.filter
									}
								>
									{f.name}
								</p>
							</div>
						))}
					</div>
					<div className={styles.SearchButtons}>
						<button
							className={`${styles.SearchButton} ${styles.CreateButton}`}
							onClick={() => createNewSite()}
						>
							Create New LSB
						</button>
						<button
							className={`${styles.SearchButton} ${styles.ExportButton}`}
							onClick={() => exportVerifiedData()}
						>
							Export Verified
						</button>
					</div>
				</div>
				<div className={styles.SearchResultsWrapper}>
					<div className={styles.SearchResultHeading}>Lineside Buildings</div>
					{searchResult && searchResult.length > 0 && (
						<div>
							{searchResult.map((res: IUserSite, idx: number) => {
								return (
									<div
										key={idx}
										className={styles.Result}
										onClick={() => {
											changeMapLocation(
												res.latitude.toString(),
												res.longitude.toString()
											);
											onSiteSelected(res);
											close();
										}}
									>
										{res.name}
									</div>
								);
							})}
						</div>
					)}
				</div>
				<div></div>
			</div>
		</div>
	);
};

export default SearchBar;
